/**
 * Рамка графика — ОДНА на весь продукт.
 *
 * До неё графиков не было вовсе: ни одного `<svg>`, а токены `--chart-*` были
 * объявлены и не использованы. Заводить марки без общей рамки значило бы
 * получить то же, что уже случилось с двухколоночными таблицами состояний, —
 * пять экранов, на каждом свой знаменатель, своя подпись и своё представление
 * о том, что такое «нет данных».
 *
 * Что рамка удерживает формой, а не договорённостью:
 *
 * * **у графика есть название, единица и знаменатель.** Все три обязательны.
 *   График без знаменателя показывает долю неизвестно от чего, а без единицы —
 *   число неизвестно чего;
 * * **легенда появляется ровно с двух рядов.** Один ряд легенды не получает:
 *   его цвет уже назван заголовком, и коробка с одним образцом повторяла бы
 *   заголовок, отнимая место;
 * * **класс доказательства едет вместе с числами.** `EvidenceBadge` и пометка
 *   ДЕМО стоят в шапке: синтетика не имеет права выглядеть как факт;
 * * **таблица — не приложение к графику, а его двойник.** Переключатель
 *   показывает ТЕ ЖЕ числа: ни одна величина не заперта в наведении;
 * * **подсказка дополняет и ничего не запирает.** Она показывается и по
 *   наведению, и по фокусу с клавиатуры;
 * * **штриховка — резервный канал опознания.** Включается человеком (полная
 *   цветовая слепота, монохромная печать), а не по умолчанию: сплошное поле
 *   штриховки читается как шум.
 *
 * Чего рамка не делает: не выбирает форму за экран и не рисует марки. Марки
 * живут в `Bars` и `Timeline`, и обе строятся на этой рамке.
 */

import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { Badge, EvidenceBadge } from "./ui";
import styles from "./chartframe.module.css";

const m = defineMessages({
  demo: { id: "common.kit.chart.demo", defaultMessage: "ДЕМО", description: "Бейдж в шапке диаграммы: показаны синтетические демо-данные; заглавными, коротко" },
  tableToggle: { id: "common.kit.chart.tableToggle", defaultMessage: "таблица", description: "Кнопка-переключатель над диаграммой: показать те же числа таблицей; строчными, коротко" },
  tableHint: { id: "common.kit.chart.tableHint", defaultMessage: "Те же числа таблицей: ни одна величина не заперта в наведении", description: "Подсказка к кнопке «таблица» над диаграммой, если экран не дал свою" },
  textureToggle: { id: "common.kit.chart.textureToggle", defaultMessage: "штриховка", description: "Кнопка-переключатель над диаграммой: заменить цвета штриховкой; строчными, коротко" },
  textureHint: { id: "common.kit.chart.textureHint", defaultMessage: "Штриховка вместо цвета: для полной цветовой слепоты и монохромной печати", description: "Подсказка к кнопке «штриховка» над диаграммой" },
});

/* ------------------------------------------------------------------ */
/* Подсказка                                                           */
/* ------------------------------------------------------------------ */

export interface ChartTooltip {
  /**
   * Показать подсказку у марки. Якорем служит сам элемент марки, а не точка
   * курсора: с клавиатуры точки курсора нет, а подсказка обязана быть та же.
   */
  show: (content: ReactNode, anchor: HTMLElement) => void;
  hide: () => void;
}

const NO_TOOLTIP: ChartTooltip = { show: () => undefined, hide: () => undefined };

const TooltipContext = createContext<ChartTooltip>(NO_TOOLTIP);

/**
 * Доступ к слою подсказок рамки. Вне рамки возвращает пустышку: марка,
 * поставленная без рамки, не должна падать — она просто останется без
 * подсказки, а её величины всё равно подписаны и лежат в таблице.
 */
export function useChartTooltip(): ChartTooltip {
  return useContext(TooltipContext);
}

interface TipState {
  content: ReactNode;
  left: number;
  top: number;
  below: boolean;
}

/* ------------------------------------------------------------------ */
/* Легенда                                                             */
/* ------------------------------------------------------------------ */

export interface ChartLegendItem {
  key: string;
  label: string;
  /**
   * `series` — ряд данных, `muted` — нейтральная дорожка отсутствия
   * (нет окна, нет знаменателя). Второе НЕ ряд: смысловой цвет ему не
   * положен, и в шкалу с рядом он не встаёт.
   */
  kind?: "series" | "muted";
  note?: ReactNode;
}

/* ------------------------------------------------------------------ */
/* Рамка                                                               */
/* ------------------------------------------------------------------ */

export function ChartFrame({
  title,
  unit,
  denominator,
  caption,
  epistemic,
  demo = false,
  legend,
  table,
  children,
  tableSummary,
}: {
  /**
   * Что нарисовано. Когда ряд ОДИН, заголовок называет именно его — легенды у
   * одного ряда не будет, и назвать его больше негде.
   */
  title: ReactNode;
  /** В чём меряем: «га», «контуров», «% площади». */
  unit: string;
  /** От чего считаем: «22 массива района», «обязательств кампании». */
  denominator: string;
  /** Смысл и ограничение. Обязательна: график без подписи — украшение. */
  caption: ReactNode;
  /** Класс доказательства ВСЕГО набора: смешивать классы в одной шкале нельзя. */
  epistemic?: string | null;
  /** Синтетика. Пометка видна всё время, пока видны числа. */
  demo?: boolean;
  /** Ряды. Легенда рисуется от двух и больше. */
  legend?: ReadonlyArray<ChartLegendItem>;
  /** Тот же набор чисел таблицей. Обязателен: величина не живёт в наведении. */
  table: ReactNode;
  /** Что показывает таблица — одной строкой, для переключателя. */
  tableSummary?: string;
  children: ReactNode;
}) {
  const t = useT();
  const [mode, setMode] = useState<"chart" | "table">("chart");
  const [textured, setTextured] = useState(false);
  const [tip, setTip] = useState<TipState | null>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const show = useCallback((content: ReactNode, anchor: HTMLElement) => {
    const plot = plotRef.current;
    if (!plot) return;
    const box = anchor.getBoundingClientRect();
    const area = plot.getBoundingClientRect();
    const top = box.top - area.top;
    setTip({
      content,
      left: box.left - area.left + box.width / 2,
      top,
      // У верхнего края подсказка разворачивается вниз, а не уезжает за полотно.
      below: top < 72,
    });
  }, []);

  const hide = useCallback(() => setTip(null), []);
  // Ссылка на слой подсказок стабильна: марки подписываются на неё в эффектах
  // и не должны перерисовываться из-за новой ссылки на каждый рендер.
  const tooltip = useMemo<ChartTooltip>(() => ({ show, hide }), [show, hide]);

  return (
    <figure
      className={`${styles.frame} ${textured ? styles.textured : ""}`}
      aria-labelledby={titleId}
    >
      <div className={styles.head}>
        <h3 className={styles.title} id={titleId}>
          {title}
        </h3>
        <span className={styles.unit}>
          {unit} · {denominator}
        </span>
        {epistemic ? <EvidenceBadge status={epistemic} /> : null}
        {demo ? <Badge tone="demo">{t(m.demo)}</Badge> : null}

        <div className={styles.controls}>
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={mode === "table"}
            onClick={() => setMode(mode === "table" ? "chart" : "table")}
            title={tableSummary ?? t(m.tableHint)}
          >
            {t(m.tableToggle)}
          </button>
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={textured}
            onClick={() => setTextured((value) => !value)}
            title={t(m.textureHint)}
          >
            {t(m.textureToggle)}
          </button>
        </div>
      </div>

      {/* Легенда — от двух рядов и всегда. Она же место, где стоит пояснение
          к нейтральной дорожке: «нет окна» это не ряд, а его отсутствие. */}
      {legend && legend.length >= 2 ? (
        <ul className={styles.legend}>
          {legend.map((item) => (
            <li key={item.key} className={styles.legendItem}>
              <span
                className={`${styles.sample} ${
                  item.kind === "muted" ? styles.sampleMuted : styles.sampleSeries
                }`}
                aria-hidden
              />
              <span>
                {item.label}
                {item.note ? (
                  <span className={styles.legendNote}> — {item.note}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className={styles.plot} ref={plotRef}>
        <TooltipContext.Provider value={tooltip}>
          {mode === "chart" ? children : table}
        </TooltipContext.Provider>

        {mode === "chart" && tip ? (
          <div
            className={styles.tip}
            style={{
              left: `${tip.left}px`,
              top: `${tip.top}px`,
              transform: tip.below
                ? "translate(-50%, calc(100% + 8px))"
                : "translate(-50%, calc(-100% - 8px))",
            }}
            // Подсказка дублирует подписанные величины и таблицу: озвучивать её
            // второй раз значит читать одно и то же дважды.
            aria-hidden
          >
            {tip.content}
          </div>
        ) : null}
      </div>

      <figcaption className={styles.caption}>{caption}</figcaption>
    </figure>
  );
}

/**
 * Строка подсказки: величина ведёт, подпись идёт следом.
 *
 * Порядок обратен легенде намеренно — у читателя уже есть ряд, он навёлся
 * ради числа.
 */
export function ChartTip({
  value,
  label,
  note,
}: {
  value: string;
  label: string;
  note?: ReactNode;
}) {
  return (
    <>
      <div className={styles.tipValue}>{value}</div>
      <div className={styles.tipLabel}>{label}</div>
      {note ? <div className={styles.tipLabel}>{note}</div> : null}
    </>
  );
}

export const chartStyles = styles;
